import React, { useEffect, useRef, useState } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import GeoJSON from 'ol/format/GeoJSON';
import WKB from 'ol/format/WKB';
import { getArea } from 'ol/sphere';
import { validatedParcelLayer } from '@/ol/layers';
import { proj2154, proj3857, proj4326 } from '@/ol/proj';
import { diagnosticParcellesState, envContraintesState, validatedAreaState } from '@/store';
import cascadedUnion from '@/utils/geometry/cascaded-union';
import { featureCollection } from '@/utils/geometry/helpers';
import { getAreaAsHectares } from '@/utils/stringutils';
import EnvContraintesTable from './Tables/EnvContraintesTable';
import ParcelleTable from './Tables/ParcelleTable';

function computeConstraintPercentByParcel(intersection, featureArea) {
  const unionIntersectionArea = Math.min(intersection.properties.area, featureArea);

  const percentsurfaceImpactee = (unionIntersectionArea / featureArea) * 100;

  return Math.round(percentsurfaceImpactee);
}

export default function EnvConstraintsAnalyzeDialog() {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

  const [open, setOpen] = useState(false);

  const [envContraintes, setEnvContraintes] = useAtom(envContraintesState);

  const setDiagnosticParcelles = useSetAtom(diagnosticParcellesState);

  const propertyArea = useAtomValue(validatedAreaState);

  const ref = useRef();

  const validatedParcelFeatures = validatedParcelLayer.getSource().getFeatures();

  const validatedParcelCount = validatedParcelFeatures.length;

  useEffect(() => {
    if (envContraintes) {
      setOpen(true);
    }
  }, [envContraintes]);

  const handleClose = () => {
    setOpen(false);
    setEnvContraintes(null);
  };

  const showContactForm = () => {
    const diagnosticParcels = [];
    validatedParcelFeatures.forEach((olFeat) => {
      const olGeom = olFeat.getGeometry();

      const featureArea = getArea(olGeom);

      const feature = new GeoJSON({
        dataProjection: proj2154,
        featureProjection: proj3857,
      }).writeFeatureObject(olFeat);

      const parcelleProps = feature.properties;

      const intersectionList = envContraintes.features
        .map((envContrainte) => ({
          features: envContrainte.intersectionParcelles[parcelleProps.id],
          featureType: envContrainte.featureType,
        }))
        .filter((envContrainte) => envContrainte.features);

      const unionIntersection = cascadedUnion(featureCollection(intersectionList.map((inter) => inter.features)));

      const geom = new WKB().writeGeometry(olGeom, {
        dataProjection: proj4326,
        featureProjection: proj3857,
        decimals: 7,
      });

      diagnosticParcels.push({
        ...parcelleProps,
        geom,
        zonages: intersectionList.map((inter) => inter.featureType),
        pourcentage_contrainte: unionIntersection
          ? computeConstraintPercentByParcel(unionIntersection, featureArea)
          : 0,
      });
    });
    setOpen(false);
    setDiagnosticParcelles({
      parcelles: diagnosticParcels,
      contraintes: {
        pourcentage_surface_impactee: envContraintes.surfaceProprieteImpactee,
        features: envContraintes.features.map((feat) => ({
          pourcentage_surface_impactee: feat.pourcentageSurfaceImpactee,
          label: feat.label,
          feature_type: feat.featureType,
        })),
      },
    });
  };

  return (
    <div ref={ref}>
      <Dialog
        container={ref.current}
        maxWidth="md"
        fullWidth={true}
        scroll="paper"
        fullScreen={fullScreen}
        open={open}
        onClose={null}
        aria-labelledby="responsive-dialog-title"
      >
        <DialogTitle id="responsive-dialog-title" color="primary">
          Analyse des enjeux environnementaux
        </DialogTitle>
        <DialogContent dividers={true}>
          <DialogContentText align="justify">
            L&apos;analyse des zonages environnementaux existants est utile pour apprécier les potentialités de chaque
            parcelle et déterminer les itinéraires techniques adapatés.
            <br />
            <br />
            {envContraintes && (
              <>
                {validatedParcelCount === 1
                  ? `La parcelle que vous venez de sélectionner est couverte sur ${envContraintes.surfaceProprieteImpactee}% de sa surface par des enjeux environnementaux.`
                  : `Les parcelles que vous venez de sélectionner sont couvertes sur ${envContraintes.surfaceProprieteImpactee}% de leur surface par des enjeux environnementaux.`}
                {envContraintes && envContraintes.features.length > 0 && (
                  <>
                    <br />
                    <br />
                    Cette liste peut ne pas être exhaustive.
                  </>
                )}
              </>
            )}
          </DialogContentText>

          {envContraintes && envContraintes.features.length > 0 && (
            <EnvContraintesTable rows={envContraintes.features} />
          )}

          <DialogContentText sx={{ mt: 3 }} color="primary">
            <b>Synthèse de votre sélection</b>
          </DialogContentText>
          <DialogContentText>Sélection totale : {getAreaAsHectares(propertyArea)}</DialogContentText>
          <ParcelleTable parcelles={validatedParcelFeatures} />
          <DialogContentText sx={{ mt: 2 }}>
            Validez votre sélection pour accéder au formulaire de contact.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={showContactForm}>
            Valider ma sélection
          </Button>
          <Button variant="outlined" onClick={handleClose}>
            Modifier ma sélection
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
